/**
 * Copy for the trial-exhausted / convert-to-Pro modal (PANDA-1500), split by
 * whether the trial pool is genuinely EMPTY or merely too LOW for the action the
 * user just tried.
 *
 * The bug this fixes: the modal always said "You've used all your trial
 * credits" — even when the user still had, say, 15 of their credits and had only
 * hit a 17-credit action. That contradicts the balance the user can see and
 * makes the upgrade prompt read as inaccurate/misleading. The non-trial credit
 * surfaces already split these two states (PANDA-1367, the `insufficientCredits`
 * shape); this brings the trial surface in line.
 *
 * The rule (acceptance criteria): NEVER say the credits are used up unless the
 * balance is actually zero. A positive-but-insufficient balance names both the
 * balance and the estimate, and offers "upgrade OR make a smaller change".
 *
 * Pure and total, so the two states are pinned by tests rather than by reading
 * JSX — mirrors lib/trial/gate-surface.ts.
 */

export type TrialExhaustedVariant = "zero_balance" | "insufficient_balance";

export interface TrialExhaustedCopyInput {
  /**
   * Trial-pool balance at the moment the gate fired. A positive value means the
   * pool still has credits — the user just can't afford THIS action. The single
   * caller (presentCreditLimit's trial_exhausted branch) always passes the
   * server's `credits_remaining`, which is 0 for a `pool_empty` block and
   * positive for an `insufficient_balance` block — so "not positive" here always
   * means a genuinely-empty pool. Null/undefined is treated the same as empty.
   */
  creditsRemaining: number | null | undefined;
  /** Credits this blocked action needed to start (the server's `min_cost`). */
  requiredCredits?: number | null;
  /** Credits granted by converting to Pro now — for the upgrade line. */
  proCredits: number;
}

export interface TrialExhaustedCopy {
  variant: TrialExhaustedVariant;
  /** The small eyebrow pill above the headline. */
  badge: string;
  /** The paragraph under the headline. */
  subheading: string;
}

export function resolveTrialExhaustedCopy(
  input: TrialExhaustedCopyInput,
): TrialExhaustedCopy {
  const { creditsRemaining, requiredCredits, proCredits } = input;
  const hasCreditsLeft =
    typeof creditsRemaining === "number" && creditsRemaining > 0;

  if (hasCreditsLeft) {
    // Only name a concrete requirement when it's genuinely higher than the
    // balance (the insufficient_balance contract). Otherwise stay vague rather
    // than print a contradictory "needs up to N" with N <= the balance shown.
    const shortfall =
      typeof requiredCredits === "number" && requiredCredits > creditsRemaining
        ? `this change needs up to ${requiredCredits} credits`
        : "that's not enough for this change";
    return {
      variant: "insufficient_balance",
      badge: "Not enough credits",
      subheading:
        `You still have ${creditsRemaining} credits, but ${shortfall}. ` +
        `Upgrade to Pro now for ${proCredits} credits and keep going — ` +
        `or make a smaller change with what you have.`,
    };
  }

  return {
    variant: "zero_balance",
    badge: "Out of trial credits",
    subheading:
      `You've used all your trial credits. Upgrade to Pro now to get ` +
      `${proCredits} credits and keep going — no waiting for day 3.`,
  };
}
